import { z } from "zod";

import { looseArray } from "../looseParse";

// Known attribute values; widened with `z.string()` so a newly introduced
// backend attribute is preserved as a raw string instead of failing the item
// and dropping the whole asset when the list is parsed via looseArray.
export const KNOWN_ASSET_ATTRIBUTES = [
  "ptp_no_exception",
  "ptp_with_exception",
  "ipo",
  "has_options",
  "options_late_close",
  "overnight_tradable",
  "overnight_halted",
] as const;

export const assetAttributeSchema = z.union([
  z.enum(KNOWN_ASSET_ATTRIBUTES),
  z.string(),
]);

export const assetStatusSchema = z.enum(["active", "inactive"]);

export const assetExchangeSchema = z.enum([
  "AMEX",
  "ARCA",
  "BATS",
  "NASDAQ",
  "NYSE",
  "OTC",
  "CRYPTO",
  "ERSX",
  "FTXU",
  "CRXL",
  "ASCX",
  "SWEEP",
]);

export const assetClassSchema = z.enum([
  "us_equity",
  "crypto",
  "us_option",
  "crypto_perp",
  "sweep",
  "treasury",
  "corporate",
  "global_equity",
  "fixed_income",
  "currency",
]);

export const assetSchema = z.object({
  id: z.string(),
  class: z.string(),
  exchange: z.string(),
  symbol: z.string(),
  name: z.string(),
  status: z.string(),
  tradable: z.boolean(),
  marginable: z.boolean(),
  maintenance_margin_requirement: z.number(),
  shortable: z.boolean(),
  easy_to_borrow: z.boolean(),
  fractionable: z.boolean(),
  // Crypto-only trading fields — absent on equity/option assets, so optional
  // (otherwise `looseArray(assetSchema)` drops every non-crypto search result).
  min_order_size: z.string().optional(),
  min_trade_increment: z.string().optional(),
  price_increment: z.string().optional(),
  attributes: z.array(assetAttributeSchema).optional(),
});

export const assetSearchResponseSchema = z.object({
  // Per-item: one malformed asset shouldn't blank the entire search response.
  assets: looseArray(assetSchema, "assetSearchResponseSchema.assets"),
  count: z.number(),
});

export const cusipResponseSchema = z.object({
  cusip: z.string(),
});
